import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, Bird, Bug, ClipboardCheck, Cog, Cow, LineChart, MapPin,
  PawPrint, Rabbit, Search, Shield, Sprout, Stethoscope, Tractor, Warehouse, Wheat,
} from "lucide-react";
import { api, cachedGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ListingCard, ListingCardSkeleton } from "@/components/ListingCard";
import AuctionShowcase from "@/components/AuctionShowcase";
import PriceCenter from "@/components/PriceCenter";
import { FreeTools } from "@/components/FreeTools";
import MarketHomeSection from "@/components/MarketHomeSection";
import NearbyListingsStrip from "@/components/NearbyListingsStrip";
import { UpcomingFairsStrip } from "@/components/fairs/UpcomingFairsStrip";
import { GrowthLeaderboard } from "@/components/GrowthLeaderboard";
import { VerificationBadges } from "@/components/VerificationBadges";
import { WeatherWidget } from "@/components/WeatherWidget";
import { PROVINCES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";

const ICONS = { Cow: Sprout, Rabbit, Bird, Bug, PawPrint, Tractor, Wheat, Warehouse, Stethoscope, Cog };

const HERO_IMG = "https://images.pexels.com/photos/27037415/pexels-photo-27037415.jpeg";

function ForumTeaser() {
  const [home, setHome] = useState(null);
  useEffect(() => {
    api.get("/forum/home").then((r) => setHome(r.data)).catch(() => {});
  }, []);
  if (!home) return null;
  const popular = (home.trending || []).slice(0, 3);
  const unanswered = (home.unanswered || []).slice(0, 3);
  return (
    <section className="mx-auto max-w-7xl px-4 pt-12 lg:px-8" data-testid="home-forum-section">
      <div className="rounded-lg border border-border bg-card p-6">
        <h2 className="font-display text-2xl font-extrabold text-primary">Ciftora Forum</h2>
        <p className="mt-1 text-base text-muted-foreground">Üreticiler bugün ne konuşuyor?</p>
        <div className="mt-5 grid gap-6 md:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase text-muted-foreground">Popüler konular</p>
            <ul className="mt-2 space-y-2">
              {popular.map((t) => (
                <li key={t.id}>
                  <Link to={`/forum/konu/${t.id}`} data-testid={`home-forum-topic-${t.id}`} className="text-sm font-semibold text-primary hover:underline">
                    {t.title}
                  </Link>
                  <span className="ml-1 text-xs text-muted-foreground">· {t.reply_count} cevap</span>
                </li>
              ))}
              {!popular.length && <li className="text-sm text-muted-foreground">Henüz konu yok.</li>}
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-muted-foreground">Cevap bekleyen sorular</p>
            <ul className="mt-2 space-y-2">
              {unanswered.map((t) => (
                <li key={t.id}>
                  <Link to={`/forum/konu/${t.id}`} data-testid={`home-forum-unanswered-${t.id}`} className="text-sm font-semibold text-primary hover:underline">
                    {t.title}
                  </Link>
                </li>
              ))}
              {!unanswered.length && <li className="text-sm text-muted-foreground">Cevapsız soru yok.</li>}
            </ul>
          </div>
        </div>
        <Button asChild className="mt-5 h-12 rounded-full px-6" data-testid="home-forum-cta">
          <Link to="/forum">Foruma Git</Link>
        </Button>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo({
    title: "Hayvan İlanları, Çiftlik Yönetimi ve Sürü Takip",
    description:
      "Ciftora ile büyükbaş, küçükbaş, kanatlı ve çiftlik ekipmanı ilanlarını keşfedin. Sürünüzü yönetin, hayvan kayıtlarını takip edin ve çiftliğinizi dijital olarak yönetin.",
    path: "/",
    image: "https://ciftora.com/brand/ciftora/social/ciftora-og-default.webp",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Ciftora",
      alternateName: "Çiftliğin Dijital Dünyası",
      url: "https://ciftora.com",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://ciftora.com/ilanlar?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  });
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("");
  const [category, setCategory] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [weatherFarms, setWeatherFarms] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/weather/farms").then((r) => setWeatherFarms(r.data || [])).catch(() => {});
  }, [user]);

  const weatherParams = weatherFarms.length
    ? { farm_id: weatherFarms[0].id }
    : { province: user?.province || "Ankara", district: user?.district || "" };

  useEffect(() => {
    api.get("/home/summary").then((r) => setData(r.data)).catch(() => {});
    cachedGet("/catalog/categories").then(setCats).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (province) p.set("province", province);
    if (category) p.set("category_id", category);
    navigate(`/ilanlar?${p.toString()}`);
  };

  const favorite = async (l) => {
    if (!user) return navigate("/giris");
    try {
      await api.post(`/favorites/${l.id}`);
      toast.success("Favorilere eklendi");
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-primary text-primary-foreground">
        <img
          src={`${HERO_IMG}?auto=compress&cs=tinysrgb&w=1280`}
          srcSet={`${HERO_IMG}?auto=compress&cs=tinysrgb&w=640 640w, ${HERO_IMG}?auto=compress&cs=tinysrgb&w=1280 1280w, ${HERO_IMG}?auto=compress&cs=tinysrgb&w=1920 1920w`}
          sizes="100vw"
          alt="Türkiye'de modern tarım"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 lg:grid-cols-[1.15fr_0.85fr] lg:px-8 lg:py-24">
          <div className="fade-up">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
              <Sprout className="h-4 w-4 text-accent" /> CİFTORA
            </p>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
              Çiftliğin
              <br />
              <span className="text-accent">Dijital Dünyası</span>
            </h1>
            <p className="mt-6 max-w-xl text-base opacity-90 md:text-lg">
              Hayvanını bul, çiftliğini yönet, verini analiz et, geleceğini planla. Türkiye'nin
              çiftlik ve hayvancılık pazaryerinde doğrulanmış satıcılar ve moderasyondan geçmiş
              güvenli ticaret.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="h-14 rounded-full bg-accent px-7 text-base font-bold text-accent-foreground hover:bg-accent/90" data-testid="hero-explore-btn">
                <Link to="/ilanlar">
                  İlanları Keşfet <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-create-btn"
                className="h-14 rounded-full border-white/40 bg-transparent px-7 text-base font-bold text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
              >
                <Link to="/ilan-ver">Ücretsiz İlan Ver</Link>
              </Button>
            </div>
            {data && (
              <div className="mt-10 flex flex-wrap gap-8" data-testid="home-stats">
                {[
                  ...(data.stats.listings
                    ? [["Yayında ilan", data.stats.listings], ["İl", data.stats.provinces]]
                    : []),
                  ...(data.stats.users >= 5 ? [["Kayıtlı kullanıcı", data.stats.users]] : []),
                  ...(data.stats.demo_listings ? [["Örnek ilan", data.stats.demo_listings]] : []),
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="font-display text-3xl font-extrabold text-accent">{value}</p>
                    <p className="text-sm opacity-75">{label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SEARCH CARD */}
          <form
            onSubmit={search}
            className="self-start rounded-lg border border-border bg-background p-6 text-foreground shadow-sm"
            data-testid="hero-search-form"
          >
            <p className="font-display text-lg font-bold text-primary">İlan Ara</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-primary">Anahtar kelime</label>
                <input
                  data-testid="home-search-keyword"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Simental besi danası"
                  className="h-12 w-full rounded-lg border border-input bg-card px-3 text-base outline-none focus:border-secondary"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-primary">Kategori</label>
                <select
                  data-testid="home-search-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="h-12 w-full rounded-lg border border-input bg-card px-3 text-base outline-none focus:border-secondary"
                >
                  <option value="">Tüm kategoriler</option>
                  {cats.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-primary">İl</label>
                <select
                  data-testid="home-search-province"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  className="h-12 w-full rounded-lg border border-input bg-card px-3 text-base outline-none focus:border-secondary"
                >
                  <option value="">Tüm iller</option>
                  {PROVINCES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" data-testid="home-search-submit" className="h-14 w-full rounded-lg text-base font-bold">
                <Search className="mr-2 h-5 w-5" /> Ara
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* HAVA + FORUM */}
      {user && (
        <section className="mx-auto max-w-7xl px-4 pt-10 lg:px-8" data-testid="home-weather-section">
          <WeatherWidget params={weatherParams} title="Çiftliğinizin Hava Durumu" />
        </section>
      )}

      <NearbyListingsStrip onFavorite={favorite} />
      <UpcomingFairsStrip />

      <ForumTeaser />

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
        <h2 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">Kategoriler</h2>
        <p className="mt-2 text-base text-muted-foreground">Aradığın hayvanı, ekipmanı veya hizmeti seç.</p>
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {(data?.category_counts || []).map(({ category: c, count }, idx) => {
            const Icon = ICONS[c.icon] || Sprout;
            return (
              <Link
                key={c.id}
                to={`/ilanlar?category_id=${c.id}`}
                data-testid={`home-category-${c.id}`}
                className={`group flex flex-col justify-between gap-6 rounded-lg border border-border bg-card p-5 transition-colors duration-200 hover:border-secondary ${
                  idx === 0 ? "md:col-span-1 lg:col-span-2 lg:row-span-1" : ""
                }`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Icon className="h-6 w-6" />
                </span>
                <span>
                  <span className="block font-display text-base font-bold text-primary">{c.name}</span>
                  <span className="text-sm text-muted-foreground">{count} ilan</span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <FreeTools />
      <PriceCenter />
      <MarketHomeSection />
      <AuctionShowcase />
      <GrowthLeaderboard />

      {/* FEATURED */}
      <Section title="Öne Çıkan İlanlar" href="/ilanlar?sort=featured" items={data?.featured} onFav={favorite} testid="featured" />
      <Section
        title={data && data.stats.listings === 0 && data.stats.demo_listings > 0 ? "Örnek İlanlar" : "Yeni İlanlar"}
        href="/ilanlar?sort=newest" items={data?.newest} onFav={favorite} testid="newest" muted />
      {!!data?.wanted?.length && (
        <Section title="Aranıyor İlanları" href="/ilanlar?listing_type=Aranıyor" items={data.wanted} onFav={favorite} testid="wanted" />
      )}

      {/* VERIFIED SELLERS */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
          <h2 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">Doğrulanmış Satıcılar</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(data?.verified_sellers || []).map((s) => (
              <div key={s.id} data-testid={`verified-seller-${s.id}`} className="rounded-lg border border-border bg-background p-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary font-display text-lg font-bold text-secondary-foreground">
                    {s.first_name?.[0]}
                  </span>
                  <div>
                    <p className="font-display font-bold text-primary">
                      {s.first_name} {s.last_name}
                    </p>
                    <p className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" /> {s.province} / {s.district}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <VerificationBadges verifications={s.verifications} compact />
                </div>
                <Link
                  to={`/satici/${s.public_slug || s.id}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline"
                >
                  İlanlarını gör <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
        <h2 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">Nasıl Çalışır?</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-4">
          {[
            ["Üye ol", "Ücretsiz hesap aç, telefon ve e-postanı doğrula."],
            ["İlanını oluştur", "14 adımlı sihirbaz ile hayvanını detaylı tanıt."],
            ["Moderasyon", "Ekibimiz her ilanı yayına almadan önce inceler."],
            ["Sat", "Mesajlaş, teklif al, karşı teklif ver, anlaş."],
          ].map(([t, d], i) => (
            <div key={t} className="rounded-lg border border-border bg-card p-6">
              <p className="font-display text-3xl font-extrabold text-accent">{i + 1}</p>
              <p className="mt-2 font-display text-base font-bold text-primary">{t}</p>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SAFE TRADE */}
      <section className="border-t border-border bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="font-display text-2xl font-extrabold sm:text-3xl">Güvenli Ticaret</h2>
            <ul className="mt-6 space-y-4">
              {[
                [Shield, "Her ilan yayına alınmadan önce moderasyon ekibimizce incelenir."],
                [BadgeCheck, "Doğrulanmış satıcı rozetleri Ciftora kontrolleriyle verilir."],
                [ClipboardCheck, "Şüpheli ilan ve mesajları tek tıkla şikâyet edin."],
              ].map(([Icon, text]) => (
                <li key={text} className="flex gap-3">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                  <span className="text-sm opacity-90 md:text-base">{text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-white/20 bg-white/5 p-8">
            <p className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase text-accent-foreground">
              <LineChart className="h-4 w-4" /> Yakında
            </p>
            <h3 className="mt-4 font-display text-xl font-extrabold sm:text-2xl">Ciftora Çiftliğim</h3>
            <p className="mt-3 text-sm opacity-90 md:text-base">
              Sürü takibi, tartım ve ADG, süt verimi, aşı ve tedavi kayıtları, yem stoğu, gelir-gider
              ve hayvan başı maliyet analizi. Ciftora Market'teki ilanlarınızla entegre çalışacak.
            </p>
            <p className="mt-4 text-xs opacity-70">
              Ciftora içi dijital kayıt sistemidir; resmî TÜRKVET kaydı veya hayvan pasaportu yerine
              geçmez.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const Section = ({ title, href, items, onFav, testid, muted }) => (
  <section className={muted ? "border-y border-border bg-card" : ""} data-testid={`home-section-${testid}`}>
    <div className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
      <div className="flex items-end justify-between gap-4">
        <h2 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">{title}</h2>
        <Link to={href} className="shrink-0 text-sm font-semibold text-secondary hover:underline">
          Tümünü gör
        </Link>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items
          ? items.map((l) => <ListingCard key={l.id} listing={l} onFavorite={onFav} />)
          : Array.from({ length: 4 }).map((_, i) => <ListingCardSkeleton key={i} />)}
      </div>
    </div>
  </section>
);
